import wx from 'jweixin-npm';
import { commonLog } from '@/services/app';
import { generateUrl, getUID, upperCaseFirst } from '@/utils';
import event from '@/utils/bus';

/**
 * 对请求参数及结果进行encode
 * @param params
 * @param res
 * @returns {{params: string}}
 */
function encodeParamsAndResponse(params, res) {
  const result = {
    params: JSON.stringify(params),
  };
  if (res) {
    result.res = JSON.stringify(res);
  }
  return result;
}

/**
 * 收集config是否成功的Log
 * @param params
 */
function collectConfigLog(params) {
  wx.ready(() => {
    commonLog('wechatConfigSuccess', encodeParamsAndResponse(params));
  });
  wx.error((res) => {
    commonLog('wechatConfigFail', encodeParamsAndResponse(params, res));
  });
}

/**
 * 修正分享链接
 * @param link
 * @returns {*}
 */
function collectShareLink(link) {
  const { location } = window;
  const { origin } = location;
  link.replace(/^http(s):\/\/(\w+.){1,}\w+/g, origin);
  return link;
}

/**
 * 设置分享参数
 * @param params
 * @param type
 * @returns {{title: string|string|*, link: *, imgUrl: string|*, fail(*=): void, trigger(): void}}
 */
function setShareParams(params, type) {
  return {
    title: params.title,
    desc: params.desc,
    link: collectShareLink(params.link),
    imgUrl: params.imgUrl,
    fail(res) {
      commonLog(`${type}Fail`, encodeParamsAndResponse(params, res));
    },
    trigger() {
      event.$emit(`trigger${upperCaseFirst(type)}`);
      params.uid = getUID();
      commonLog(`${type}Trigger`, encodeParamsAndResponse(params));
    },
  };
}


class WXJSSDK {
  constructor() {
    this.config = {
      appId: '',
      timestamp: '',
      nonceStr: '',
      signature: '',
    };
    this.params = {
      title: '',
      desc: '',
      link: '',
      imgUrl: '',
      qZoneTitle: '',
    };
    this.qZoneParams = {};
    this.qqParams = {};
    this.weiboParams = {};
    this.wxFriendsParams = {};
    this.wxTimelineParams = {};
    this.payParams = {};
  }

  changeConfigParams(config) {
    this.config = config;
  }

  setDefaultParams(params) {
    this.params.title = params.title || '';
    this.params.desc = params.desc || '';
    this.params.link = params.link || window.location.href;
    this.params.imgUrl = params.imgUrl || '';
    this.params.qZoneTitle = params.qZoneTitle || '';
  }

  setParams(data) {
    const params = {};
    params.title = data.title || this.params.title;
    params.desc = data.desc || this.params.desc;
    params.link = data.link || this.params.link;
    params.imgUrl = data.imgUrl || this.params.imgUrl;
    params.qZoneTitle = data.qZoneTitle || this.params.qZoneTitle;
    return params;
  }

  changeParams(data) {
    this.qZoneParams = this.setParams(data.qZoneParams);
    this.qqParams = this.setParams(data.qqParams);
    this.weiboParams = this.setParams(data.weiboParams);
    this.wxFriendsParams = this.setParams(data.wxFriendsParams);
    this.wxTimelineParams = this.setParams(data.wxTimelineParams);
    this.wechatMenuShareTimeline();
    this.wechatMenuShareAppMessage();
    this.wechatMenuShareQQ();
    this.wechatMenuShareWeibo();
    this.wechatMenuShareQZone();
  }

  changePayParams(params) {
    this.payParams = params;
  }

  configWeChat() {
    const { config } = this;
    wx.config({
      debug: false,
      appId: config.appId,
      timestamp: config.timestamp,
      nonceStr: config.nonceStr,
      signature: config.signature,
      jsApiList: ['onMenuShareAppMessage', 'onMenuShareTimeline', 'onMenuShareQQ', 'onMenuShareQZone', 'chooseWXPay', 'startRecord', 'stopRecord', 'onVoiceRecordEnd', 'playVoice', 'pauseVoice', 'stopVoice', 'onVoicePlayEnd', 'uploadVoice', 'downloadVoice'],
    });
    collectConfigLog(config);
  }

  wechatMenuShareTimeline() {
    const { wxTimelineParams } = this;
    const params = setShareParams(wxTimelineParams, 'shareTimeline');
    wx.ready(() => {
      wx.onMenuShareTimeline(params);
    });
  }

  wechatMenuShareAppMessage() {
    const { wxFriendsParams } = this;
    const params = setShareParams(wxFriendsParams, 'shareAppMessage');
    wx.ready(() => {
      wx.onMenuShareAppMessage(params);
    });
  }

  getWechatMenuShareAppParams() {
    return this.wxFriendsParams;
  }

  getWechatMenuShareAppLink() {
    const params = this.wxFriendsParams;
    return params.link;
  }

  wechatMenuShareQQ() {
    const { qqParams } = this;
    const params = setShareParams(qqParams, 'shareAppMessage');
    wx.ready(() => {
      wx.onMenuShareQQ(params);
    });
  }

  getWebWeiboShareUrl() {
    const params = this.weiboParams;
    const url = 'http://service.weibo.com/share/share.php';
    const query = {
      url: params.link,
      title: params.title,
      pic: params.imgUrl,
    };
    return generateUrl(url, query);
  }

  wechatMenuShareWeibo() {
    const { weiboParams } = this;
    const params = setShareParams(weiboParams, 'ShareWeibo');
    wx.ready(() => {
      wx.onMenuShareWeibo(params);
    });
  }

  getWebQZoneShareUrl() {
    const params = this.qZoneParams;
    const url = 'http://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey';
    const query = {
      url: params.link,
      title: params.title,
      summary: params.desc,
      desc: params.productTitle,
      pics: params.imgUrl,
    };
    return generateUrl(url, query);
  }

  wechatMenuShareQZone() {
    const { qZoneParams } = this;
    const params = setShareParams(qZoneParams, 'shareAppMessage');
    wx.ready(() => {
      wx.onMenuShareQZone(params);
    });
  }

  wxPay() {
    const params = this.payParams;
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wx.chooseWXPay({
          timestamp: params.timestamp,
          nonceStr: params.nonceStr,
          package: params.package,
          signType: params.signType,
          paySign: params.paySign,
          success(res) {
            commonLog('chooseWXPaySuccess', encodeParamsAndResponse(params, res));
            return resolve(res);
          },
          fail(res) {
            commonLog('chooseWXPayFail', encodeParamsAndResponse(params, res));
            return reject(res);
          },
          cancel(res) {
            commonLog('chooseWXPayCancel', encodeParamsAndResponse(params, res));
            return reject(res);
          },
        });
      });
    });
  }

  startRecord() {
    const { config } = this;
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wx.startRecord({
          success() {
            return resolve();
          },
          fail(res) {
            commonLog('startRecordFail', encodeParamsAndResponse(config, res));
            return reject(res);
          },
          cancel(res) {
            commonLog('startRecordCancel', encodeParamsAndResponse(config, res));
            return reject(res);
          },
        });
      });
    });
  }

  stopRecord() {
    const { config } = this;
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wx.stopRecord({
          success(res) {
            return resolve(res);
          },
          fail(res) {
            commonLog('stopRecordFail', encodeParamsAndResponse(config, res));
            return reject(res);
          },
          cancel(res) {
            commonLog('stopRecordCancel', encodeParamsAndResponse(config, res));
            return reject(res);
          },
        });
      });
    });
  }

  onVoiceRecordEnd() {
    const { config } = this;
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wx.onVoiceRecordEnd({
          complete(res) {
            return resolve(res);
          },
          fail(res) {
            commonLog('onVoiceRecordEndFail', encodeParamsAndResponse(config, res));
            return reject(res);
          },
          cancel(res) {
            commonLog('onVoiceRecordEndCancel', encodeParamsAndResponse(config, res));
            return reject(res);
          },
        });
      });
    });
  }

  playVoice(localId) {
    const { config } = this;
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wx.playVoice({
          localId,
          success(res) {
            return resolve(res);
          },
          fail(res) {
            commonLog('playVoiceFail', encodeParamsAndResponse(config, res));
            return reject(res);
          },
          cancel(res) {
            commonLog('playVoiceCancel', encodeParamsAndResponse(config, res));
            return reject(res);
          },
        });
      });
    });
  }

  pauseVoice(localId) {
    const { config } = this;
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wx.pauseVoice({
          localId,
          success(res) {
            return resolve(res);
          },
          fail(res) {
            commonLog('pauseVoiceFail', encodeParamsAndResponse(config, res));
            return reject(res);
          },
          cancel(res) {
            commonLog('pauseVoiceCancel', encodeParamsAndResponse(config, res));
            return reject(res);
          },
        });
      });
    });
  }

  stopVoice(localId) {
    const { config } = this;
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wx.stopVoice({
          localId,
          success(res) {
            return resolve(res);
          },
          fail(res) {
            commonLog('stopVoiceFail', encodeParamsAndResponse(config, res));
            return reject(res);
          },
          cancel(res) {
            commonLog('stopVoiceCancel', encodeParamsAndResponse(config, res));
            return reject(res);
          },
        });
      });
    });
  }

  onVoicePlayEnd() {
    const { config } = this;
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wx.onVoicePlayEnd({
          success(res) {
            return resolve(res);
          },
          fail(res) {
            commonLog('onVoicePlayEndFail', encodeParamsAndResponse(config, res));
            return reject(res);
          },
          cancel(res) {
            commonLog('onVoicePlayEndCancel', encodeParamsAndResponse(config, res));
            return reject(res);
          },
        });
      });
    });
  }

  uploadVoice(localId, isShowProgressTips = 1) {
    const { config } = this;
    return new Promise((resolve, reject) => {
      wx.ready(() => {
        wx.uploadVoice({
          localId,
          isShowProgressTips, // 默认为1，显示进度提示
          success(res) {
            return resolve(res);
          },
          fail(res) {
            commonLog('uploadVoiceFail', encodeParamsAndResponse(config, res));
            return reject(res);
          },
          cancel(res) {
            commonLog('uploadVoiceCancel', encodeParamsAndResponse(config, res));
            return reject(res);
          },
        });
      });
    });
  }
}

const wechat = new WXJSSDK();
export default wechat;
